import cx from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Input, Tooltip } from "reactstrap";

import global from "../../scss/dhp.scss";

import { Icon as UiIcon } from "../../components/ui/icon";
import { generateTableObj, getSiblings } from "../../_helpers/utils";
import { fetchWidgetList } from "../../store/actions/editorActions";
import WidgetComponent from "../../components/Editor/Widgets/handleWidgetRedirection";
import Text from "../../components/Editor/Widgets/Text";
import AiWriter from "../../components/Editor/Widgets/AiWriter";
import AiTranslate from "../../components/Editor/Widgets/AiTranslate";
import TextFrame from "../../components/Editor/Widgets/TextFrame";
import Background from "../../components/Editor/Widgets/Background";
import Line from "../../components/Editor/Widgets/Line";
import Shape from "../../components/Editor/Widgets/Shape";
import Icon from "../../components/Editor/Widgets/Icon";
import Illustration from "../../components/Editor/Widgets/Illustration";
import Picture from "../../components/Editor/Widgets/Picture";
import Upload from "../../components/Editor/Widgets/Upload";
import Video from "../../components/Editor/Widgets/Video";
import PageTransition from "../../components/Editor/Widgets/PageTransition";
import PageMargin from "../../components/Editor/Widgets/PageMargin";
import Version from "../../components/Editor/Widgets/Version";
import Sticker from "../../components/Editor/Widgets/Sticker";
import Animation from "../../components/Editor/Widgets/Animation";
import Audio from "../../components/Editor/Widgets/Audio";
import Form from "../../components/Editor/Widgets/Form";
import QrCode from "../../components/Editor/Widgets/QrCode";
import * as constant from "../../constants/editor";
import { EditorContext } from "./EditorLayout";
import Tour from "../../components/User/Tour";
import { TR_EDITOR_STEP_1 } from "../../constants/user";
import TypeformSvg from "../../assets/images/Typeform-Symbol.svg";
import BrandKit from "../../components/Editor/Widgets/BrandKit";
import Theme from "../../components/Editor/Widgets/Theme";
import JotformSvg from "../../assets/images/Jotform-Symbol.svg";
import Collage from "../../components/Editor/Widgets/Collage";
import WidgetAnimation from "../../components/Editor/Widgets/WidgetAnimation";
import useHandleWidgetAction from "../../hooks/useHandleWidgetAction";
import Modal from "../../components/ui/modal";
import TableModalComponent from "../../components/Editor/Widgets/TableV2";
import Map from "../../components/Editor/Widgets/Map";
import PageResizeComponent from "../../components/Editor/PageResizeComponent";
import PricingTable from "../../components/Editor/Widgets/PricingTable";
import Chart from "../../components/Editor/Widgets/Chart";
import Signature from "../../components/Editor/Widgets/Signature";
import TextArt from "../../components/Editor/Widgets/TextArt";
import Frame from "../../components/Editor/Widgets/Frame";
import MergeField from "../../components/Editor/Widgets/MergeField";
import Timeline from "../../components/Editor/Widgets/Timeline";
import ScopeOfWork from "../../components/Editor/Widgets/ScopeOfWork";
import Itinerary from "../../components/Editor/Widgets/Itinerary";
import Button from "../../components/Editor/Widgets/Button";
import CallOut from "../../components/Editor/Widgets/CallOut";
import Portfolio from "../../components/Editor/Widgets/Portfolio";
import CaseStudy from "../../components/Editor/Widgets/CaseStudy";
import Countdown from "../../components/Editor/Widgets/Countdown";

let style = Object.assign({}, global);

export const EditorSidebarContext = React.createContext({});

const svgIconList = {
  typeforms: TypeformSvg,
  jotforms: JotformSvg,
};

const WidgetList = ({
  slug,
  icon,
  name,
  items,
  sidebarGroupToggleHandler,
  handleWidgetAction,
  index,
  availableWidgetLists,
  toggleModal,
  setTableConfig,
  toggleMapModal,
  setMapConfig,
  togglePageResizeModal,
  togglePricingTableModal,
  setPricingTableConfig,
  toggleChartModal,
  setChartConfig,
  toggleTextArtModal,
  setTextConfig,
  toggleTimelineModal,
  setTimelineConfig,
  toggleScopeOfWorkModal,
  setScopeOfWorkConfig,
  toggleItineraryModal,
  setItineraryConfig,
  togglePortfolioModal,
  setPortfolioConfig,
  toggleCaseStudyModal,
  setCaseStudyConfig,
}) => {
  let { documentType, widgets } = useContext(EditorContext);

  const [pricingTableInDoc, setPricingTableInDoc] = useState();
  const [timelineInDoc, setTimelineInDoc] = useState();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTimeline, setTooltipTimeline] = useState(false);

  let filterItem;
  // for infographic, interactive demo and proposal type doc
  if ([constant.TYPE_INFOGRAPHIC, constant.TYPE_PROPOSAL, constant.TYPE_INTERACTIVE_DEMO].includes(documentType)) {
    if ([constant.TYPE_INFOGRAPHIC, constant.TYPE_INTERACTIVE_DEMO].includes(documentType))
      filterItem = items.filter(
        item =>
          item.slug !== "pricing-table" &&
          item.slug !== "timeline" &&
          item.slug !== "scope-of-work" &&
          item.slug !== "itinerary" &&
          item.slug !== "portfolio" &&
          item.slug !== "case-study" &&
          item.slug !== "audio"
      ); // no pricing table, timeline no audio - for infographic and interactive demo type doc
    if ([constant.TYPE_PROPOSAL].includes(documentType)) filterItem = items.filter(item => item.slug !== "audio"); // no audio - for proposal doc
  }
  //for other type doc
  else {
    filterItem = items.filter(
      item =>
        item.slug !== "pricing-table" &&
        item.slug !== "timeline" &&
        item.slug !== "scope-of-work" &&
        item.slug !== "itinerary" &&
        item.slug !== "portfolio" &&
        item.slug !== "case-study"
    );
  }

  useEffect(() => {
    let isPricingTable = false;
    let isTimeline = false;
    widgets.forEach(widget => {
      if (widget.data["data-asset-type"] === constant.PRICING_TABLE) {
        isPricingTable = true;
        return;
      }

      if (widget.data["data-asset-type"] === constant.TIMELINE) {
        isTimeline = true;
        return;
      }
    });
    setPricingTableInDoc(isPricingTable);
    setTimelineInDoc(isTimeline);
  }, [widgets]);

  const toggle = () => {
    setTooltipOpen(!tooltipOpen);
  };
  const toggleTimeline = () => {
    setTooltipTimeline(!tooltipTimeline);
  };

  availableWidgetLists = [constant.TYPE_PROPOSAL].includes(documentType)
    ? availableWidgetLists?.filter(availableWidgetList => availableWidgetList.slug !== "interactions")
    : [constant.TYPE_INTERACTIVE_DEMO].includes(documentType)
      ? availableWidgetLists?.filter(availableWidgetList => availableWidgetList.slug !== "signature")
      : availableWidgetLists?.filter(
          availableWidgetList => !["signature", "interactions"].includes(availableWidgetList.slug)
        );

  return (
    <li key={slug} className={style["template-group-item"]} id={index}>
      <Link to="#" onClick={e => sidebarGroupToggleHandler(e)}>
        <UiIcon icon={icon} />
        {name}
        <UiIcon additionalclass="ms-auto me-0" icon="ui-arrow-right" />
      </Link>
      <ul className={cx(style["groupType"], style["widget-group"], style["clearfix"])} id={`widget-${index}`}>
        {filterItem.map(widgetitem => (
          <>
            <li
              key={widgetitem.name}
              id={
                pricingTableInDoc && widgetitem.slug === "pricing-table"
                  ? "p-table"
                  : timelineInDoc && widgetitem.slug === "timeline"
                    ? "timeline"
                    : widgetitem.name
              }
              className={cx(style["widget-group-item"], style["asset-" + widgetitem.slug], {
                [style["disable"]]:
                  (pricingTableInDoc && widgetitem.slug === "pricing-table") ||
                  (timelineInDoc && widgetitem.slug === "timeline"),
              })}>
              {widgetitem?.new_release && <span className={style["new-tag"]}>New</span>}
              <Link
                to="#"
                onMouseDown={e => {
                  if (
                    (pricingTableInDoc && widgetitem.slug === "pricing-table") ||
                    (timelineInDoc && widgetitem.slug === "timeline")
                  )
                    return;
                  e.preventDefault();
                  setTableConfig();
                  setMapConfig();
                  setPricingTableConfig();
                  setChartConfig();
                  setTimelineConfig();
                  setScopeOfWorkConfig();
                  setItineraryConfig();
                  setPortfolioConfig();
                  setCaseStudyConfig();
                  (setTextConfig(),
                    handleWidgetAction(
                      widgetitem.slug,
                      widgetitem.name,
                      widgetitem.asset_type,
                      availableWidgetLists.findIndex(
                        availableWidgetList => availableWidgetList.slug.toLowerCase() === slug.toLowerCase()
                      ),
                      false,
                      slug,
                      false,
                      toggleModal,
                      toggleMapModal,
                      togglePageResizeModal,
                      togglePricingTableModal,
                      toggleChartModal,
                      toggleTimelineModal,
                      toggleScopeOfWorkModal,
                      toggleItineraryModal,
                      togglePortfolioModal,
                      toggleCaseStudyModal,
                      toggleTextArtModal
                    ));
                }}>
                {constant.THIRD_PARTY_APPS.includes(widgetitem.asset_type) && (
                  <img src={svgIconList[widgetitem.asset_type]} width="30" alt="" />
                )}
                {!constant.THIRD_PARTY_APPS.includes(widgetitem.asset_type) && (
                  <UiIcon icon={widgetitem.icon.split("icon-")[1]} />
                )}

                <span className={style["widget-text"]}>{widgetitem.name}</span>
              </Link>
              {pricingTableInDoc && widgetitem.slug === "pricing-table" && (
                <Tooltip
                  className="pricing-table-tooltip-sidebar"
                  placement="bottom"
                  isOpen={tooltipOpen}
                  target="p-table"
                  toggle={toggle}
                  boundariesElement={document.getElementById("app")}>
                  You can add only one Pricing Table
                </Tooltip>
              )}
              {timelineInDoc && widgetitem.slug === "timeline" && (
                <Tooltip
                  className="pricing-table-tooltip-sidebar"
                  placement="bottom"
                  isOpen={tooltipTimeline}
                  target="timeline"
                  toggle={toggleTimeline}
                  boundariesElement={document.getElementById("app")}>
                  You can add only one Timeline
                </Tooltip>
              )}
            </li>
          </>
        ))}
      </ul>
    </li>
  );
};
//Props type validation
WidgetList.propTypes = {
  slug: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  sidebarGroupToggleHandler: PropTypes.func.isRequired,
  handleWidgetAction: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  availableWidgetLists: PropTypes.array,
};

const EditorSidebar = props => {
  let {
    documentType,
    templateGroupName,
    isDocumentReady,
    metadata,
    uploadState,
    setUploadState,
    widgets,
    showMergeFieldSelectorModal,
    showMergeFieldInfoModal,
    showMfBulkUpdateModal,
    showMissingMergeFieldModal,
    disableEditor,
  } = useContext(EditorContext);

  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    handleWidgetAction,
    widgetActionClicked,
    returnFromAsset,
    activeAssetName,
    activeAssetSchemeType,
    activeAssetType,
    groupIndex,
  } = useHandleWidgetAction();

  const exceptRedirectionWidgets = [
    "Table",
    "PageSize",
    "Map",
    "Chart",
    "PricingTable",
    "Timeline",
    "ScopeOfWork",
    "Portfolio",
    "CaseStudy",
  ];
  const availableWidgetLists = useSelector(state => state?.editor?.availableWidgetLists);
  const isCompSuperAdmin = useSelector(state => state.auth?.user?.company?.superadmin_company);

  const [searchWidget, setSearchWidget] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalFullScreen, setModalFullScreen] = useState(false);
  const [tableConfig, setTableConfig] = useState();
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapConfig, setMapConfig] = useState();
  const [showPageResizeModal, setShowPageResizeModal] = useState(false);
  const [showPricingTableModal, setShowPricingTableModal] = useState(false);
  const [pricingTableConfig, setPricingTableConfig] = useState(false);
  const [activeColorThemePageIdx, setActiveColorThemePageIdx] = useState();
  const [showChartModal, setShowChartModal] = useState(false);
  const [chartConfig, setChartConfig] = useState();
  const [showTextArtModal, setShowTextArtModal] = useState(false);
  const [textConfig, setTextConfig] = useState();
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineConfig, setTimelineConfig] = useState(false);
  const [showScopeOfWorkModal, setShowScopeOfWorkModal] = useState(false);
  const [scopeOfWorkConfig, setScopeOfWorkConfig] = useState(false);
  const [showItineraryModal, setShowItineraryModal] = useState(false);
  const [itineraryConfig, setItineraryConfig] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [portfolioConfig, setPortfolioConfig] = useState(false);
  const [showCaseStudyModal, setShowCaseStudyModal] = useState(false);
  const [caseStudyConfig, setCaseStudyConfig] = useState(false);

  const moduleSubTitle =
    templateGroupName === constant.TYPE_BUSINESS_ESSENTIALS ? documentType : templateGroupName.replace("-", " ");
  const components = {
    Text: Text,
    AiWriter: AiWriter,
    AiTranslate: AiTranslate,
    TextFrame: TextFrame,
    Background: Background,
    Line: Line,
    Shape: Shape,
    Icon: Icon,
    Sticker: Sticker,
    Illustration: Illustration,
    Picture: Picture,
    Upload: Upload,
    Video: Video,
    Animation: Animation,
    Audio: Audio,
    QrCode: QrCode,
    Collage: Collage,
    PageTransition: PageTransition,
    PageMargin: PageMargin,
    Version: Version,
    Typeform: Form,
    BrandKit: BrandKit,
    Theme: Theme,
    Jotform: Form,
    WidgetAnimation: WidgetAnimation,
    Signature: Signature,
    Frame: Frame,
    MergeField: MergeField,
    Button: Button,
    Callout: CallOut,
    Countdown: Countdown,
  };

  let w_id = metadata.activeWidgetId[0];
  let isGroupWidget = document.getElementById(w_id)?.closest(".dhp-page-group");
  let targetId = isGroupWidget ? document.getElementById(w_id).closest(".dhp-root-widget").getAttribute("id") : w_id;
  let targetWidgetIndex = widgets.findIndex(widget => widget.id === targetId);
  let widgetLockStatus = widgets[targetWidgetIndex]?.data["data-layer-locked"];

  // toggle menu function
  const sidebarGroupToggleHandler = event => {
    event.preventDefault();

    let closestSibiling = event.target.closest(".template-group-item");
    let siblings = getSiblings(closestSibiling);
    let dataObj = {
      documentId: id,
      index: closestSibiling.id,
    };

    Array.from(siblings).forEach(el => el.classList.remove("active"));
    closestSibiling.classList.toggle("active");
    sessionStorage.setItem("dataObj", JSON.stringify(dataObj));
  };

  // Toggle modal func
  const toggleModal = () => {
    setShowModal(prevState => !prevState);
  };

  // Toggle pricing table modal function
  const togglePricingTableModal = () => {
    setShowPricingTableModal(prevState => !prevState);
  };

  // Toggle timeline modal function
  const toggleTimelineModal = () => {
    setShowTimelineModal(prevState => !prevState);
  };

  // Toggle scope of work modal function
  const toggleScopeOfWorkModal = () => {
    setShowScopeOfWorkModal(prevState => !prevState);
  };

  // Toggle itinerary modal function
  const toggleItineraryModal = () => {
    setShowItineraryModal(prevState => !prevState);
  };

  // Toggle portfolio modal function
  const togglePortfolioModal = () => {
    setShowPortfolioModal(prevState => !prevState);
  };

  // Toggle CaseStudy modal function
  const toggleCaseStudyModal = () => {
    setShowCaseStudyModal(prevState => !prevState);
  };

  // Toggle Map modal func
  const toggleMapModal = () => {
    setShowMapModal(prevState => !prevState);
  };

  const toggleChartModal = () => {
    setShowChartModal(prevState => !prevState);
  };

  // Toggle PageResize modal func
  const togglePageResizeModal = () => {
    setShowPageResizeModal(prevState => !prevState);
  };

  // Toggle Text Art modal func
  const toggleTextArtModal = () => {
    setShowTextArtModal(prevState => !prevState);
  };

  const handleBackgroundClick = () => {
    handleWidgetAction("background", "Background", "backgrounds", 1, false, "graphics-media");
  };

  const handleTransitionClick = () => {
    handleWidgetAction("page-transition", "Page Transition", "page_transition", 5, false, "page-setup");
  };

  const handleAnimationClick = animationSchemeType => {
    handleWidgetAction(
      "widget-animation",
      "Widget Animation",
      "widget_animation",
      1,
      false,
      "graphics-media",
      animationSchemeType
    );
  };

  const handleQrIconClick = () => {
    handleWidgetAction("qr-code", "QR Code", "qr_code", 1, false, "graphics-media");
  };

  const handleDoubleClick = e => {
    if (document.getElementById(metadata.activeWidgetId[0])?.getAttribute("data-layer-locked") === "true") return;
    if (
      constant.TABLE === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      setTableConfig(generateTableObj(metadata.activeWidgetId[0]));
      toggleModal();
    }
    if (
      constant.MAP === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      setMapConfig(document.getElementById(metadata.activeWidgetId[0]));
      toggleMapModal();
    }
    if (
      constant.PRICING_TABLE === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      // find closest input group
      const group = e.target.closest(".input-group");

      if (group) {
        // check if this group has increment/decrement buttons
        const hasControls = group.querySelector(".btn-increment") || group.querySelector(".btn-decrement");

        if (hasControls) {
          e.stopPropagation();
          return; // block modal
        }
      }

      let pricingTableDataConfig = document.getElementById(metadata.activeWidgetId)?.getAttribute("data-table-config");
      setPricingTableConfig(JSON.parse(pricingTableDataConfig));
      togglePricingTableModal();
    }
    if (
      constant.CHART === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      setChartConfig(document.getElementById(metadata.activeWidgetId[0]));
      toggleChartModal();
    }
    if (
      constant.TEXT_ART === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      setTextConfig(document.getElementById(metadata.activeWidgetId[0]));
      toggleTextArtModal();
    }
    if (
      constant.TIMELINE === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      let timelineDataConfig = document.getElementById(metadata.activeWidgetId)?.getAttribute("data-table-config");
      setTimelineConfig(JSON.parse(timelineDataConfig));
      toggleTimelineModal();
    }
    if (
      constant.SCOPE_OF_WORK === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      let scopeOfWorkDataConfig = document.getElementById(metadata.activeWidgetId)?.getAttribute("data-table-config");
      setScopeOfWorkConfig(JSON.parse(scopeOfWorkDataConfig));
      toggleScopeOfWorkModal();
    }
    if (
      constant.ITINERARY === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      let itineraryDataConfig = document.getElementById(metadata.activeWidgetId)?.getAttribute("data-table-config");
      setItineraryConfig(JSON.parse(itineraryDataConfig));
      toggleItineraryModal();
    }
    if (
      constant.PORTFOLIO === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      let portfolioDataConfig = document.getElementById(metadata.activeWidgetId)?.getAttribute("data-table-config");
      setPortfolioConfig(JSON.parse(portfolioDataConfig));
      togglePortfolioModal();
    }
    if (
      constant.CASESTUDY === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      let caseStudyDataConfig = document.getElementById(metadata.activeWidgetId)?.getAttribute("data-table-config");
      setCaseStudyConfig(JSON.parse(caseStudyDataConfig));
      toggleCaseStudyModal();
    }
    if (
      constant.CALLOUT === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      if (document.getElementById("editorBody").classList.contains("hide-secondAside")) props.toggle();
      handleWidgetAction("callout", "Callouts", "callouts", 2, false, "interactions");
    }
    if (
      constant.QR_CODE === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      if (document.getElementById("editorBody").classList.contains("hide-secondAside")) props.toggle();
      handleWidgetAction("qr-code", "QR Code", "qr_code", 1, false, "graphics-media");
    }
    if (
      constant.COUNTDOWN === metadata.activeWidgetType[0] &&
      !document.getElementById(metadata.activeWidgetId[0]).closest(".dhp-page-group")
    ) {
      if (document.getElementById("editorBody").classList.contains("hide-secondAside")) props.toggle();
      handleWidgetAction("countdown", "Countdown", "countdown", 1, false, "graphics-media");
    }
  };

  const handleOutsideAssetUpload = () => {
    handleWidgetAction("upload", "Upload", "uploads", 1, false, "graphics-media");
  };

  const handleVideoEditorPlayClick = () => {
    handleWidgetAction("*", false, false, 0, "returnToWidgetList");
  };

  const handleCalloutIconClick = () => {
    handleWidgetAction("callout", "Callouts", "callouts", 2, false, "interactions");
  };

  const handleCountdownClick = () => {
    handleWidgetAction("countdown", "Countdown", "countdown", 1, false, "graphics-media");
  };

  useEffect(() => {
    // back to main left pain from widget animation if locked widget is active
    if (
      document.querySelector(".editor-asset-inner.widgetanimation-asset") &&
      metadata.activeWidgetId.length === 1 &&
      widgetLockStatus
    ) {
      handleWidgetAction("*", false, false, false, "returnToWidgetList");
    }
  }, [metadata.activeWidgetId, widgetLockStatus]);

  // redirect to sidebar from outside widgets click like background, qr, page transition, animation etc
  useEffect(() => {
    document.getElementById("toolbar_background")?.addEventListener("click", handleBackgroundClick);
    document
      .getElementById(`doc-timeline-transition-icon${metadata.activePageIdx}`)
      ?.addEventListener("click", handleTransitionClick);
    document
      .getElementById(`page-timeline-entrance-animation-icon-${metadata.activeWidgetId[0]}`)
      ?.addEventListener("click", e => {
        handleAnimationClick("entrance");
      });
    document
      .getElementById(`page-timeline-exit-animation-icon-${metadata.activeWidgetId[0]}`)
      ?.addEventListener("click", e => {
        handleAnimationClick("exit");
      });
    document.getElementById("qr-toolbar")?.addEventListener("click", handleQrIconClick);

    document.getElementById(metadata.activeWidgetId[0])?.addEventListener("dblclick", handleDoubleClick);
    document
      .getElementById(`page-top-transition-icon-${metadata.activePageIdx}`)
      ?.addEventListener("click", handleTransitionClick);
    document.querySelector(".page-timeline-player")?.addEventListener("click", handleVideoEditorPlayClick);
    document.querySelector(".document-timeline-player")?.addEventListener("click", handleVideoEditorPlayClick);
    document
      .querySelector(`#page-top-play-icon-${metadata.activePageIdx}`)
      ?.addEventListener("click", handleVideoEditorPlayClick);
    document.getElementById("animate-toolbar")?.addEventListener("click", e => {
      handleAnimationClick("entrance");
    });
    window?.addEventListener("click", e => {
      if (e.target.getAttribute("id") === "animate-toolbar" || e.target.closest("#animate-toolbar")) {
        handleAnimationClick("entrance");
      }
    });
    document.getElementById("callout-toolbar")?.addEventListener("click", handleCalloutIconClick);
    document.getElementById("countdown-toolbar")?.addEventListener("click", handleCountdownClick);

    return () => {
      document.getElementById("toolbar_background")?.removeEventListener("click", handleBackgroundClick);
      document
        .getElementById(`doc-timeline-transition-icon${metadata.activePageIdx}`)
        ?.removeEventListener("click", handleTransitionClick);
      document.getElementById("qr-toolbar")?.removeEventListener("click", handleQrIconClick);
      document.getElementById(metadata.activeWidgetId[0])?.removeEventListener("dblclick", handleDoubleClick);
      document
        .getElementById(`page-top-transition-icon-${metadata.activePageIdx}`)
        ?.removeEventListener("click", handleTransitionClick);
      document.querySelector(".page-timeline-player")?.removeEventListener("click", handleVideoEditorPlayClick);
      document.querySelector(".document-timeline-player")?.addEventListener("click", handleVideoEditorPlayClick);
      document
        .querySelector(`#page-top-play-icon-${metadata.activePageIdx}`)
        ?.removeEventListener("click", handleVideoEditorPlayClick);
      document.getElementById("animate-toolbar")?.removeEventListener("click", e => {
        handleAnimationClick("entrance");
      });
      window?.removeEventListener("click", e => {
        handleAnimationClick("entrance");
      });
      document.getElementById("callout-toolbar")?.removeEventListener("click", handleCalloutIconClick);
      document.getElementById("countdown-toolbar")?.removeEventListener("click", handleCountdownClick);
    };
  });

  //Redirects outside asset upload to sidebar
  useEffect(() => {
    if (uploadState?.isOutSideAssetUpload) {
      handleOutsideAssetUpload();
      setUploadState({ ...uploadState, isOutSideAssetUpload: false });
    }
  }, [uploadState?.isOutSideAssetUpload]);

  //onload selected group will open
  useEffect(() => {
    let dataObj,
      index = 0;

    if (sessionStorage.dataObj) {
      dataObj = JSON.parse(sessionStorage.getItem("dataObj"));

      if (dataObj.documentId !== id) {
        sessionStorage.removeItem("dataObj");
        dataObj = false;
      } else index = dataObj.index;
    }

    if (dataObj && dataObj.slug) {
      let i = availableWidgetLists?.find(availableWidgetList =>
        availableWidgetList.items.find(item => item.slug === dataObj.slug)
      );
      handleWidgetAction(dataObj.slug, dataObj.name, dataObj.type, dataObj.index, false, i?.slug);
    } else {
      Array.from(document.getElementsByClassName("template-group-item")).forEach(el => el.classList.remove("active"));
      document.getElementsByClassName("template-group-item")[index]?.classList.add("active");
    }
  }, []);

  // on search widget every time 1st group remain open
  useEffect(() => {
    if (searchedWidgetLists && searchWidget) {
      let dataObj = {
        documentId: id,
        index: 0,
      };
      Array.from(document.getElementsByClassName("template-group-item")).forEach(el => el.classList.remove("active"));
      document.getElementsByClassName("template-group-item")[0]?.classList.add("active");
      sessionStorage.setItem("dataObj", JSON.stringify(dataObj));
    }
  }, [searchWidget]);

  // Every time we back from any widget, respective parent group remain open
  useEffect(() => {
    if (returnFromAsset) {
      setSearchWidget("");

      var dataObj = {
        documentId: id,
        index: groupIndex,
      };

      sessionStorage.setItem("dataObj", JSON.stringify(dataObj));

      setTimeout(() => {
        document.getElementsByClassName("template-group-item")[groupIndex]?.classList.add("active");
      }, 10);
    }
  }, [widgetActionClicked]);

  // Fix company switch issue from uploads
  useEffect(() => {
    if (!availableWidgetLists) {
      dispatch(fetchWidgetList(isCompSuperAdmin));
    }
  }, [availableWidgetLists]);

  // Available widget list modification based on widget search result
  let searchedWidgetLists = [constant.TYPE_PROPOSAL].includes(documentType)
    ? availableWidgetLists?.filter(availableWidgetList => availableWidgetList.slug !== "interactions")
    : [constant.TYPE_INTERACTIVE_DEMO].includes(documentType)
      ? availableWidgetLists?.filter(availableWidgetList => availableWidgetList.slug !== "signature")
      : availableWidgetLists?.filter(
          availableWidgetList => !["signature", "interactions"].includes(availableWidgetList.slug)
        );
  searchedWidgetLists = searchedWidgetLists
    ?.map(group => ({
      ...group,
      items: group?.items?.filter(
        child =>
          child.name.toLowerCase().includes(searchWidget.toLowerCase()) && !child?.excluded?.includes(documentType)
      ),
    }))
    .filter(group => group?.items?.length > 0);

  // for merge fields - START
  useEffect(() => {
    if (showMergeFieldSelectorModal || showMergeFieldInfoModal || showMfBulkUpdateModal || showMissingMergeFieldModal) {
      handleWidgetAction("merge-field", "Merge Field", "merge_field", 0, false, "text", "system");
    } else {
      handleWidgetAction("*", false, false, 0, "returnToWidgetList");
    }
  }, [showMergeFieldSelectorModal, showMergeFieldInfoModal, showMfBulkUpdateModal, showMissingMergeFieldModal]);
  // for meerge fields - END

  return (
    <EditorSidebarContext.Provider value={{ activeColorThemePageIdx, setActiveColorThemePageIdx }}>
      <div
        className={cx(
          style["dhp-secondAside"],
          { [style["pe-none"]]: !isDocumentReady },
          "disable-while-video-editor-playing",
          { [style["custom-disable"]]: disableEditor },
        )}>
        <div className={style["module-title"]}>
          <h2 className={cx(style["fw-7"], style["m-0"])}>Documents</h2>
          <span className={style["moduleSub-title"]}>{moduleSubTitle}</span>
        </div>

        {(widgetActionClicked === "*" || exceptRedirectionWidgets.includes(widgetActionClicked)) && (
          <div className={style["editor-aside-content"]}>
            <div className={cx(style["search-field"], style["pb-3"])}>
              <div className={cx(style["search-area"], style["clearfix"])}>
                <span className={style["searchicon"]}>
                  <UiIcon icon="ui-search" />
                </span>
                <Input
                  type="text"
                  id="widgetSearch"
                  className={style["form-control"]}
                  placeholder="Search Widgets"
                  onChange={e => setSearchWidget(e.target.value)}
                  autoComplete="off" //need to discard this autocomplete once ui input field is marged
                />
              </div>
            </div>
            <div className={cx(style["customScroll"], style["scroll-Y"])}>
              <nav className={cx(style["template-groupNav"], style["pt-0"])}>
                <ul
                  className={cx(style["nav"], style["flex-column"], style["h-100"], style["editor-sidenav-initmenu"])}>
                  {searchedWidgetLists?.length > 0 &&
                    searchedWidgetLists?.map((searchedWidgetList, index) => (
                      <WidgetList
                        key={searchedWidgetList.slug}
                        slug={searchedWidgetList.slug}
                        icon={searchedWidgetList.icon.split("icon-")[1]}
                        name={searchedWidgetList.name}
                        items={searchedWidgetList.items}
                        sidebarGroupToggleHandler={sidebarGroupToggleHandler}
                        handleWidgetAction={handleWidgetAction}
                        index={index}
                        availableWidgetLists={availableWidgetLists}
                        toggleModal={toggleModal}
                        setTableConfig={setTableConfig}
                        toggleMapModal={toggleMapModal}
                        setMapConfig={setMapConfig}
                        togglePageResizeModal={togglePageResizeModal}
                        togglePricingTableModal={togglePricingTableModal}
                        setPricingTableConfig={setPricingTableConfig}
                        toggleChartModal={toggleChartModal}
                        setChartConfig={setChartConfig}
                        toggleTextArtModal={toggleTextArtModal}
                        setTextConfig={setTextConfig}
                        toggleTimelineModal={toggleTimelineModal}
                        setTimelineConfig={setTimelineConfig}
                        toggleScopeOfWorkModal={toggleScopeOfWorkModal}
                        setScopeOfWorkConfig={setScopeOfWorkConfig}
                        toggleItineraryModal={toggleItineraryModal}
                        setItineraryConfig={setItineraryConfig}
                        togglePortfolioModal={togglePortfolioModal}
                        setPortfolioConfig={setPortfolioConfig}
                        toggleCaseStudyModal={toggleCaseStudyModal}
                        setCaseStudyConfig={setCaseStudyConfig}
                      />
                    ))}
                  {searchedWidgetLists?.length === 0 && (
                    <div className={cx(style["alert"], style["alert-block"], style["alert-danger"])}>
                      No results found
                    </div>
                  )}
                </ul>
              </nav>
              <Tour targetId="widget-0" tourType={TR_EDITOR_STEP_1} />
            </div>
          </div>
        )}
        {widgetActionClicked !== "*" && !exceptRedirectionWidgets.includes(widgetActionClicked) && (
          <WidgetComponent
            component={components[widgetActionClicked]}
            assetName={activeAssetName}
            assetType={activeAssetType}
            handleWidgetAction={handleWidgetAction}
            assetSchemeType={activeAssetSchemeType}
          />
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <Modal
          size="xl"
          toggle={toggleModal}
          showModal={showModal}
          component={TableModalComponent}
          setShowModal={setShowModal}
          closeModal={toggleModal}
          backdrop="static"
          modalClassName={
            modalFullScreen ? "tabel-modal modal-with-leftpane full-screen-modal" : "tabel-modal modal-with-leftpane"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={tableConfig}
          documentType={documentType}
        />
      )}

      {showMapModal && (
        <Modal
          size="xl"
          toggle={toggleMapModal}
          showModal={showMapModal}
          component={Map}
          setShowModal={setShowMapModal}
          closeModal={toggleMapModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal modal-with-leftpane full-screen-modal map-modal"
              : "tabel-modal modal-with-leftpane map-modal"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editable={mapConfig}
        />
      )}

      {showPricingTableModal && (
        <Modal
          size="xl"
          toggle={togglePricingTableModal}
          showModal={showPricingTableModal}
          component={PricingTable}
          setShowModal={setShowPricingTableModal}
          closeModal={togglePricingTableModal}
          backdrop="static"
          modalClassName={
            modalFullScreen ? "tabel-modal pricing-table-modal full-screen-modal" : "tabel-modal pricing-table-modal"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={pricingTableConfig}
        />
      )}

      {showTimelineModal && (
        <Modal
          size="xl"
          toggle={toggleTimelineModal}
          showModal={showTimelineModal}
          component={Timeline}
          setShowModal={setShowTimelineModal}
          closeModal={toggleTimelineModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal pricing-table-modal timeline-modal full-screen-modal"
              : "tabel-modal pricing-table-modal timeline-modal"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={timelineConfig}
        />
      )}

      {showScopeOfWorkModal && (
        <Modal
          size="xl"
          toggle={toggleScopeOfWorkModal}
          showModal={showScopeOfWorkModal}
          component={ScopeOfWork}
          setShowModal={setShowScopeOfWorkModal}
          closeModal={toggleScopeOfWorkModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal pricing-table-modal scope-of-work-modal full-screen-modal"
              : "tabel-modal pricing-table-modal scope-of-work-modal"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={scopeOfWorkConfig}
        />
      )}

      {showItineraryModal && (
        <Modal
          size="xxl"
          toggle={toggleItineraryModal}
          showModal={showItineraryModal}
          component={Itinerary}
          setShowModal={setShowItineraryModal}
          closeModal={toggleItineraryModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal itinerary-modal modal-with-leftpane full-screen-modal"
              : "tabel-modal itinerary-modal modal-with-leftpane "
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={itineraryConfig}
        />
      )}

      {showPortfolioModal && (
        <Modal
          size="xxl"
          toggle={togglePortfolioModal}
          showModal={showPortfolioModal}
          component={Portfolio}
          setShowModal={setShowPortfolioModal}
          closeModal={togglePortfolioModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal itinerary-modal portfolio-modal modal-with-leftpane full-screen-modal"
              : "tabel-modal itinerary-modal portfolio-modal modal-with-leftpane "
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={portfolioConfig}
        />
      )}

      {showCaseStudyModal && (
        <Modal
          size="xxl"
          toggle={toggleCaseStudyModal}
          showModal={showCaseStudyModal}
          component={CaseStudy}
          setShowModal={setShowCaseStudyModal}
          closeModal={toggleCaseStudyModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal itinerary-modal portfolio-modal case-study-modal modal-with-leftpane full-screen-modal"
              : "tabel-modal itinerary-modal portfolio-modal case-study-modal modal-with-leftpane "
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editTable={caseStudyConfig}
        />
      )}

      {showChartModal && (
        <Modal
          size="xl"
          toggle={toggleChartModal}
          showModal={showChartModal}
          component={Chart}
          setShowModal={setShowChartModal}
          closeModal={toggleChartModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal modal-with-leftpane full-screen-modal chart-modal"
              : "tabel-modal modal-with-leftpane chart-modal"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editable={chartConfig}
        />
      )}

      {showPageResizeModal && (
        <PageResizeComponent showModal={showPageResizeModal} setShowModal={setShowPageResizeModal} />
      )}

      {showTextArtModal && (
        <Modal
          size="xl"
          toggle={toggleTextArtModal}
          showModal={showTextArtModal}
          component={TextArt}
          setShowModal={setShowTextArtModal}
          closeModal={toggleTextArtModal}
          backdrop="static"
          modalClassName={
            modalFullScreen
              ? "tabel-modal modal-with-leftpane full-screen-modal text-art-modal"
              : "tabel-modal modal-with-leftpane text-art-modal"
          }
          modalFullScreen={modalFullScreen}
          setModalFullScreen={setModalFullScreen}
          editable={textConfig}
        />
      )}
    </EditorSidebarContext.Provider>
  );
};
//Props type validation
EditorSidebar.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
};

export default EditorSidebar;
